import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useSiteAlerts, type SiteAlert } from "@/hooks/use-supabase-data";
import {
  BellRing,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  CalendarRange,
} from "lucide-react";

const alertVariants = [
  { value: "info", label: "Information" },
  { value: "success", label: "Celebration" },
  { value: "warning", label: "Warning" },
  { value: "danger", label: "Urgent" },
] as const;

type AlertVariant = (typeof alertVariants)[number]["value"];

interface AlertFormState {
  title: string;
  message: string;
  is_active: boolean;
  start_at: string;
  end_at: string;
  variant: AlertVariant;
  action_label: string;
  action_url: string;
}

const defaultFormState: AlertFormState = {
  title: "",
  message: "",
  is_active: true,
  start_at: "",
  end_at: "",
  variant: "info",
  action_label: "",
  action_url: "",
};

export function AlertsManager() {
  const { alerts, loading, error, refetch } = useSiteAlerts();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<SiteAlert | null>(null);
  const [formState, setFormState] = useState<AlertFormState>(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState<"error" | "success" | "">("");

  useEffect(() => {
    if (!isDialogOpen) {
      setFormState(defaultFormState);
      setEditingAlert(null);
    }
  }, [isDialogOpen]);

  const upcomingAlerts = useMemo(() => {
    return alerts.slice().sort((a, b) => {
      const startA = a.start_at ? new Date(a.start_at).getTime() : 0;
      const startB = b.start_at ? new Date(b.start_at).getTime() : 0;
      return startB - startA;
    });
  }, [alerts]);

  const handleRefresh = async () => {
    setStatusMessage("");
    setStatusType("");
    await refetch();
  };

  const handleInputChange =
    <K extends keyof AlertFormState>(field: K) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value =
        event.target.type === "checkbox"
          ? (event.target as HTMLInputElement).checked
          : event.target.value;
      setFormState((prev) => ({
        ...prev,
        [field]: value as AlertFormState[K],
      }));
    };

  const handleSwitchChange = (value: boolean) => {
    setFormState((prev) => ({
      ...prev,
      is_active: value,
    }));
  };

  const handleEdit = (alert: SiteAlert) => {
    setEditingAlert(alert);
    setFormState({
      title: alert.title ?? "",
      message: alert.message ?? "",
      is_active: alert.is_active ?? true,
      start_at: alert.start_at ? toLocalInputValue(alert.start_at) : "",
      end_at: alert.end_at ? toLocalInputValue(alert.end_at) : "",
      variant: (alert.variant as AlertVariant) ?? "info",
      action_label: alert.action_label ?? "",
      action_url: alert.action_url ?? "",
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingAlert(null);
    setFormState(defaultFormState);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("site_alerts").delete().eq("id", id);
      if (error) throw error;
      setStatusType("success");
      setStatusMessage("Alert deleted.");
      await refetch();
    } catch (_err) {
      setStatusType("error");
      setStatusMessage("Failed to delete alert.");
    }
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setStatusMessage("");
    setStatusType("");

    if (!formState.title.trim()) {
      setStatusType("error");
      setStatusMessage("Title is required.");
      setSaving(false);
      return;
    }

    if (!formState.message.trim()) {
      setStatusType("error");
      setStatusMessage("Message is required.");
      setSaving(false);
      return;
    }

    const payload = {
      title: formState.title.trim(),
      message: formState.message.trim(),
      is_active: formState.is_active,
      start_at: formState.start_at ? new Date(formState.start_at).toISOString() : null,
      end_at: formState.end_at ? new Date(formState.end_at).toISOString() : null,
      variant: formState.variant,
      action_label: formState.action_label.trim() || null,
      action_url: formState.action_url.trim() || null,
      updated_at: new Date().toISOString(),
    };

    if (payload.action_label && !payload.action_url) {
      setStatusType("error");
      setStatusMessage("Please provide a link URL when setting an action label.");
      setSaving(false);
      return;
    }

    if (payload.action_url && !payload.action_label) {
      setStatusType("error");
      setStatusMessage("Please provide a button label when setting an action URL.");
      setSaving(false);
      return;
    }

    try {
      if (editingAlert?.id) {
        const { error } = await supabase.from("site_alerts").update(payload).eq("id", editingAlert.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("site_alerts").insert({
          ...payload,
          created_at: new Date().toISOString(),
        });
        if (error) throw error;
      }

      setIsDialogOpen(false);
      setStatusType("success");
      setStatusMessage("Alert saved successfully.");
      await refetch();
    } catch (_err) {
      setStatusType("error");
      setStatusMessage("Failed to save alert. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">
            Site Alerts
          </h2>
          <p className="text-slate-600 text-lg">
            Configure popup announcements shown to visitors.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Alert
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {statusMessage && (
        <Alert
          className={
            statusType === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : statusType === "error"
                ? undefined
                : undefined
          }
          variant={statusType === "error" ? "destructive" : "default"}
        >
          {statusType === "success" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="w-5 h-5" />
            Alerts ({alerts.length})
          </CardTitle>
          <CardDescription>Manage active and scheduled site alerts.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <CalendarRange className="h-6 w-6 animate-spin mr-3" />
              Loading alerts...
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-10">
              <BellRing className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No alerts configured.</p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Alert
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>CTA</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingAlerts.map((alert) => {
                  const scheduleLabel = buildScheduleLabel(alert.start_at, alert.end_at);
                  const isCurrentlyActive = computeIsActive(alert);
                  return (
                    <TableRow key={alert.id}>
                      <TableCell>
                        <div className="font-medium">{alert.title}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={alert.is_active && isCurrentlyActive ? "default" : "secondary"}
                          className={
                            alert.is_active && isCurrentlyActive
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : ""
                          }
                        >
                          {alert.is_active ? (isCurrentlyActive ? "Active" : "Scheduled") : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {alert.variant ?? "info"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{scheduleLabel}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {alert.message}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {alert.action_label && alert.action_url ? (
                          <a
                            href={alert.action_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            {alert.action_label}
                          </a>
                        ) : (
                          <span className="text-muted-foreground/70">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(alert)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete alert?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove the alert from the site.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
                                  onClick={() => handleDelete(alert.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{editingAlert ? "Edit Alert" : "Add Alert"}</AlertDialogTitle>
            <AlertDialogDescription>
              Configure the content and schedule for this site alert.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="alert-title">Title</Label>
                <Input
                  id="alert-title"
                  value={formState.title}
                  onChange={handleInputChange("title")}
                  placeholder="Village Announcement"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alert-active">Active</Label>
                <div className="flex items-center gap-2 rounded-md border border-input px-3 py-2">
                  <Switch id="alert-active" checked={formState.is_active} onCheckedChange={handleSwitchChange} />
                  <span className="text-sm text-muted-foreground">
                    {formState.is_active ? "Visible when within schedule" : "Hidden"}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="alert-message">Message</Label>
              <Textarea
                id="alert-message"
                rows={4}
                value={formState.message}
                onChange={handleInputChange("message")}
                placeholder="Describe the important notice for villagers..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="alert-variant">Alert Style</Label>
                <select
                  id="alert-variant"
                  value={formState.variant}
                  onChange={handleInputChange("variant")}
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                >
                  {alertVariants.map((variant) => (
                    <option key={variant.value} value={variant.value}>
                      {variant.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="alert-start">Start Time</Label>
                <Input
                  id="alert-start"
                  type="datetime-local"
                  value={formState.start_at}
                  onChange={handleInputChange("start_at")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alert-end">End Time</Label>
                <Input
                  id="alert-end"
                  type="datetime-local"
                  value={formState.end_at}
                  onChange={handleInputChange("end_at")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alert-action-label">Action Button Label</Label>
                <Input
                  id="alert-action-label"
                  value={formState.action_label}
                  onChange={handleInputChange("action_label")}
                  placeholder="e.g., Check Now"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alert-action-url">Action URL</Label>
                <Input
                  id="alert-action-url"
                  value={formState.action_url}
                  onChange={handleInputChange("action_url")}
                  placeholder="https://example.com/new-scheme"
                />
              </div>
            </div>

            <AlertDialogFooter className="pt-4">
              <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Alert"}
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function toLocalInputValue(dateString: string) {
  const date = new Date(dateString);
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

function buildScheduleLabel(start_at: string | null, end_at: string | null) {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  const startLabel = start_at ? new Date(start_at).toLocaleString(undefined, options) : "Immediate";
  const endLabel = end_at ? new Date(end_at).toLocaleString(undefined, options) : "No end date";

  return `${startLabel} – ${endLabel}`;
}

function computeIsActive(alert: SiteAlert) {
  const now = new Date();
  const inSchedule =
    (!alert.start_at || new Date(alert.start_at) <= now) &&
    (!alert.end_at || new Date(alert.end_at) >= now);
  return alert.is_active && inSchedule;
}

